import { useState, useMemo } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clientiFinaliApi, committentiApi, workOrdersApi } from '../services/api'
import { ClienteFinale } from '../types'
import { Link } from 'react-router-dom'
import {
  Plus, Search, MapPin, Phone, Star, AlertTriangle,
  Pencil, Trash2, X,
  RefreshCw, Navigation, ArrowUpDown, ArrowUp, ArrowDown,
  ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

type SortCol = 'frazionario' | 'indirizzo' | 'tipologia' | 'gps' | null
type SortDir = 'asc' | 'desc'

const TIPOLOGIE: Record<string, string> = {
  residenziale: 'Residenziale', business: 'Business', sede_postale: 'Sede Postale',
  ufficio_pubblico: 'Ufficio Pubblico', sede_istituzionale: 'Istituzionale',
}

const STATO_COLORS: Record<string, string> = {
  ricevuto:  'bg-slate-100 text-slate-600',
  assegnato: 'bg-blue-100 text-blue-700',
  sospeso:   'bg-amber-100 text-amber-700',
  annullato: 'bg-red-100 text-red-600',
  eseguito:  'bg-emerald-100 text-emerald-700',
}

// ── Pratiche espandibili per un cliente ───────────────────────────────────────
function PraticheRow({ clienteId, colSpan }: { clienteId: number; colSpan: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['wo-by-cliente', clienteId],
    queryFn: () =>
      workOrdersApi.list({ cliente_finale_id: clienteId, per_page: 1000 }).then(r => r.data.items),
    staleTime: 30_000,
  })

  if (isLoading) return (
    <tr><td colSpan={colSpan} className="py-4 text-center text-slate-400 bg-slate-50/60">
      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
    </td></tr>
  )

  const items = data ?? []

  return (
    <tr>
      <td colSpan={colSpan} className="px-0 py-0 bg-slate-50/60 border-b border-slate-200">
        <div className="px-6 py-3">
          <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
            {items.length} {items.length === 1 ? 'Pratica' : 'Pratiche'}
          </p>
          {items.length === 0 ? (
            <p className="text-sm text-slate-400 italic">Nessuna pratica collegata a questo cliente.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="pb-1 pr-3 font-semibold">N°</th>
                  <th className="pb-1 pr-3 font-semibold">Tipologia</th>
                  <th className="pb-1 pr-3 font-semibold">Stato</th>
                  <th className="pb-1 pr-3 font-semibold">Data</th>
                  <th className="pb-1 pr-3 font-semibold">Tecnico</th>
                  <th className="pb-1 font-semibold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((wo: any) => (
                  <tr key={wo.id} className="hover:bg-slate-100/60">
                    <td className="py-1 pr-3 font-mono font-medium text-slate-700">{wo.numero_progressivo}</td>
                    <td className="py-1 pr-3 text-slate-600">{wo.tipologia}</td>
                    <td className="py-1 pr-3">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${STATO_COLORS[wo.stato] ?? 'bg-slate-100 text-slate-600'}`}>
                        {wo.stato}
                      </span>
                    </td>
                    <td className="py-1 pr-3 text-slate-500">{wo.data_appuntamento ?? '—'}</td>
                    <td className="py-1 pr-3 text-slate-500">{wo.tecnico_nome ?? '—'}</td>
                    <td className="py-1">
                      <Link
                        to={`/pratiche/${wo.id}`}
                        className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800"
                        title="Apri pratica"
                      >
                        <ExternalLink size={11} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </td>
    </tr>
  )
}

// ── Pagina principale ─────────────────────────────────────────────────────────
export default function ClientiFinali() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const [search, setSearch] = useState('')
  const [committenteFilter, setCommittenteFilter] = useState('')
  const [filtroGps, setFiltroGps] = useState<'tutti' | 'senza' | 'con'>('tutti')
  const [filtroFrazionario, setFiltroFrazionario] = useState<'tutti' | 'senza' | 'con'>('tutti')
  const [filtroIndirizzo, setFiltroIndirizzo] = useState<'tutti' | 'senza' | 'con'>('tutti')
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<ClienteFinale | null>(null)
  const [sortCol, setSortCol] = useState<SortCol>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const isReadonly = user?.role === 'readonly'
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const toggleExpand = (id: number) =>
    setExpanded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleSelect = (id: number) =>
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleAll = () =>
    setSelected(s => s.size === sorted.length ? new Set() : new Set(sorted.map(c => c.id)))

  const deleteMutation = useMutation({
    mutationFn: (id: number) => clientiFinaliApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clienti-finali'] }); toast.success('Cliente eliminato') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => clientiFinaliApi.bulkDelete(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clienti-finali'] })
      setSelected(new Set())
      toast.success('Clienti eliminati')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const deleteAllMutation = useMutation({
    mutationFn: () => clientiFinaliApi.deleteAll(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clienti-finali'] })
      setSelected(new Set())
      toast.success('Tutti i clienti eliminati')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const syncMutation = useMutation({
    mutationFn: () => clientiFinaliApi.syncFromPratiche(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['clienti-finali'] })
      const d = res.data
      toast.success(`Creati ${d.creati}, ${d.collegati} WO collegati. Geocodifica avviata per ${d.da_geocodificare} indirizzi.`, { duration: 6000 })
    },
    onError: () => toast.error('Errore sincronizzazione'),
  })

  const geocodeOneMutation = useMutation({
    mutationFn: (id: number) => clientiFinaliApi.geocodeOne(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clienti-finali'] }); toast.success('Coordinate aggiornate') },
    onError: () => toast.error('Geocodifica non riuscita'),
  })

  const bulkGeocodeMutation = useMutation({
    mutationFn: (ids: number[]) => clientiFinaliApi.bulkGeocode(ids),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['clienti-finali'] })
      toast.success(`Geocodifica avviata per ${res.data.avviati} clienti.`, { duration: 5000 })
    },
    onError: () => toast.error('Errore geocodifica'),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['clienti-finali', search, committenteFilter],
    queryFn: () => clientiFinaliApi.list({
      search, page: 1, per_page: 9999,
      ...(committenteFilter && { committente_id: committenteFilter }),
    }).then(r => r.data),
  })

  const { data: committenti } = useQuery({
    queryKey: ['committenti-select'],
    queryFn: () => committentiApi.list({ per_page: 100 }).then(r => r.data.items),
  })

  const items: ClienteFinale[] = data?.items ?? []

  const filtered = useMemo(() => {
    return items.filter(c => {
      const haGps = !!(c.lat && c.lng)
      if (filtroGps === 'senza' && haGps) return false
      if (filtroGps === 'con' && !haGps) return false
      const haFrazionario = !!c.codice_sito
      if (filtroFrazionario === 'senza' && haFrazionario) return false
      if (filtroFrazionario === 'con' && !haFrazionario) return false
      const indirizzoMancante = !c.indirizzo || c.indirizzo.trim() === '' || c.indirizzo.trim().toUpperCase() === 'N/D'
      if (filtroIndirizzo === 'senza' && !indirizzoMancante) return false
      if (filtroIndirizzo === 'con' && indirizzoMancante) return false
      return true
    })
  }, [items, filtroGps, filtroFrazionario, filtroIndirizzo])

  const sorted = useMemo(() => {
    if (!sortCol) return filtered
    return [...filtered].sort((a, b) => {
      let cmp = 0
      if (sortCol === 'frazionario') {
        const av = (a.codice_sito || '').toLowerCase()
        const bv = (b.codice_sito || '').toLowerCase()
        cmp = av < bv ? -1 : av > bv ? 1 : 0
        if (!a.codice_sito && b.codice_sito) cmp = 1
        if (a.codice_sito && !b.codice_sito) cmp = -1
      } else if (sortCol === 'indirizzo') {
        const av = ((a.indirizzo || '') + (a.comune || '')).toLowerCase()
        const bv = ((b.indirizzo || '') + (b.comune || '')).toLowerCase()
        cmp = av < bv ? -1 : av > bv ? 1 : 0
      } else if (sortCol === 'tipologia') {
        const av = (TIPOLOGIE[a.tipologia_sito] || a.tipologia_sito || '').toLowerCase()
        const bv = (TIPOLOGIE[b.tipologia_sito] || b.tipologia_sito || '').toLowerCase()
        cmp = av < bv ? -1 : av > bv ? 1 : 0
      } else if (sortCol === 'gps') {
        const aHas = !!(a.lat && a.lng)
        const bHas = !!(b.lat && b.lng)
        cmp = aHas === bHas ? 0 : aHas ? -1 : 1
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortCol, sortDir])

  const cycleSort = (col: SortCol) => {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc') }
    else if (sortDir === 'asc') setSortDir('desc')
    else { setSortCol(null) }
  }

  const SortIcon = ({ col }: { col: SortCol }) => {
    if (sortCol !== col) return <ArrowUpDown size={13} className="text-slate-400" />
    return sortDir === 'asc'
      ? <ArrowUp size={13} className="text-blue-500" />
      : <ArrowDown size={13} className="text-blue-500" />
  }

  const selectedWithoutGps = Array.from(selected).filter(id => {
    const c = sorted.find(x => x.id === id)
    return c && (!c.lat || !c.lng)
  })

  const COL_SPAN = 10

  return (
    <div className="space-y-4">
      {ConfirmModal}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Clienti Finali</h1>
          <p className="text-slate-500 text-sm">
            {filtered.length !== items.length ? `${filtered.length} di ${items.length} clienti` : `${data?.total ?? 0} clienti totali`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {selected.size > 0 && selectedWithoutGps.length > 0 && (
            <button
              onClick={() => bulkGeocodeMutation.mutate(selectedWithoutGps)}
              className="btn-secondary flex items-center gap-1.5"
              disabled={bulkGeocodeMutation.isPending}
              title={`Geocodifica ${selectedWithoutGps.length} clienti selezionati senza coordinate`}
            >
              <Navigation size={14} className={bulkGeocodeMutation.isPending ? 'animate-pulse' : ''} />
              <span>Geocodifica {selectedWithoutGps.length} senza GPS</span>
            </button>
          )}
          {isAdmin && selected.size > 0 && (
            <button
              onClick={() => confirmDelete(`Eliminare ${selected.size} clienti selezionati?`, () => bulkDeleteMutation.mutate(Array.from(selected)))}
              className="btn-secondary" style={{ color: '#ef4444' }}
              disabled={bulkDeleteMutation.isPending}
            >
              <Trash2 size={15} /><span className="ml-1">Elimina {selected.size}</span>
            </button>
          )}
          {selected.size > 0 && (
            <button onClick={() => setSelected(new Set())} className="btn-ghost text-sm flex items-center gap-1">
              <X size={13} /> Deseleziona
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => confirmDelete('Eliminare TUTTI i clienti? Operazione irreversibile.', () => deleteAllMutation.mutate())}
              className="btn-secondary" style={{ color: '#ef4444' }}
              disabled={deleteAllMutation.isPending}
            >
              <Trash2 size={15} /><span className="ml-1">Cancella tutto</span>
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => { if (window.confirm('Importa e sincronizza tutti i clienti dalle Pratiche?')) syncMutation.mutate() }}
              className="btn-secondary"
              disabled={syncMutation.isPending}
            >
              <RefreshCw size={15} className={syncMutation.isPending ? 'animate-spin' : ''} />
              <span className="ml-1">{syncMutation.isPending ? 'Sincronizzazione...' : 'Sincronizza da Pratiche'}</span>
            </button>
          )}
          {!isReadonly && <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={16} />Nuovo Cliente</button>}
        </div>
      </div>

      {/* Filtri */}
      <div className="card p-4 flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input pl-9" placeholder="Cerca per nome, indirizzo, comune..."
            value={search} onChange={(e) => { setSearch(e.target.value) }} />
        </div>
        <select className="select w-48" value={committenteFilter}
          onChange={(e) => { setCommittenteFilter(e.target.value) }}>
          <option value="">Tutti i committenti</option>
          {committenti?.map((c: { id: number; ragione_sociale: string }) =>
            <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
        </select>
        <select className="select w-40" value={filtroGps} onChange={(e) => setFiltroGps(e.target.value as any)}>
          <option value="tutti">GPS: tutti</option>
          <option value="senza">Senza GPS</option>
          <option value="con">Con GPS</option>
        </select>
        <select className="select w-44" value={filtroFrazionario} onChange={(e) => setFiltroFrazionario(e.target.value as any)}>
          <option value="tutti">Frazionario: tutti</option>
          <option value="senza">Senza frazionario</option>
          <option value="con">Con frazionario</option>
        </select>
        <select className="select w-44" value={filtroIndirizzo} onChange={(e) => setFiltroIndirizzo(e.target.value as any)}>
          <option value="tutti">Indirizzo: tutti</option>
          <option value="senza">Senza indirizzo</option>
          <option value="con">Con indirizzo</option>
        </select>
        {(filtroGps !== 'tutti' || filtroFrazionario !== 'tutti' || filtroIndirizzo !== 'tutti') && (
          <button
            className="btn-ghost text-sm flex items-center gap-1"
            onClick={() => { setFiltroGps('tutti'); setFiltroFrazionario('tutti'); setFiltroIndirizzo('tutti') }}
          >
            <X size={13} /> Reset filtri
          </button>
        )}
      </div>

      {/* Tabella */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50/50">
                <th className="px-3 py-3 w-8">
                  <input type="checkbox"
                    checked={sorted.length > 0 && selected.size === sorted.length}
                    onChange={toggleAll} className="cursor-pointer" />
                </th>
                <th className="px-2 py-3 w-8"></th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Cliente</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">
                  <button onClick={() => cycleSort('frazionario')} className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                    Frazionario<SortIcon col="frazionario" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">
                  <button onClick={() => cycleSort('indirizzo')} className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                    Indirizzo<SortIcon col="indirizzo" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">
                  <button onClick={() => cycleSort('tipologia')} className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                    Tipologia<SortIcon col="tipologia" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Contatto</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Flag</th>
                <th className="px-4 py-3 font-semibold text-slate-600">
                  <button onClick={() => cycleSort('gps')}
                    className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                    title="asc = senza GPS prima, desc = con GPS prima">
                    GPS<SortIcon col="gps" />
                  </button>
                </th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={COL_SPAN} className="py-12 text-center text-slate-400">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={COL_SPAN} className="py-12 text-center text-slate-400">Nessun cliente trovato</td></tr>
              ) : sorted.map((c) => (
                <>
                  <tr key={c.id}
                    className="table-row-hover"
                    style={{ background: selected.has(c.id) ? 'rgba(26,115,232,0.05)' : undefined }}>

                    {/* Checkbox */}
                    <td className="px-3 py-3 w-8">
                      <input type="checkbox" checked={selected.has(c.id)}
                        onChange={() => toggleSelect(c.id)} className="cursor-pointer" />
                    </td>

                    {/* Espandi/comprimi pratiche */}
                    <td className="px-2 py-3 w-8">
                      <button
                        onClick={() => toggleExpand(c.id)}
                        className="btn-ghost px-1 py-1 text-slate-400 hover:text-blue-600"
                        title={expanded.has(c.id) ? 'Nascondi pratiche' : 'Mostra pratiche'}
                      >
                        {expanded.has(c.id)
                          ? <ChevronUp size={15} />
                          : <ChevronDown size={15} />}
                      </button>
                    </td>

                    {/* Nome */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim() || '—'}
                      </div>
                      {c.telefono && (
                        <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                          <Phone size={10} />{c.telefono}
                        </div>
                      )}
                    </td>

                    {/* Frazionario */}
                    <td className="px-4 py-3 text-xs font-mono text-slate-500">{c.codice_sito || '—'}</td>

                    {/* Indirizzo */}
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-1.5">
                        <MapPin size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-slate-700">{c.indirizzo}</div>
                          <div className="text-xs text-slate-400">{c.cap} {c.comune} ({c.provincia})</div>
                        </div>
                      </div>
                    </td>

                    {/* Tipologia */}
                    <td className="px-4 py-3">
                      <span className="badge bg-indigo-50 text-indigo-700">
                        {TIPOLOGIE[c.tipologia_sito] || c.tipologia_sito}
                      </span>
                    </td>

                    {/* Referente */}
                    <td className="px-4 py-3 text-xs text-slate-500">{c.referente_locale || '—'}</td>

                    {/* Flag */}
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {c.flag_vip && <span className="badge bg-yellow-100 text-yellow-700"><Star size={10} className="mr-0.5" />VIP</span>}
                        {c.flag_critico && <span className="badge bg-red-100 text-red-700"><AlertTriangle size={10} className="mr-0.5" />Critico</span>}
                      </div>
                    </td>

                    {/* GPS */}
                    <td className="px-4 py-3">
                      {c.lat && c.lng ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600"
                          title={`${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`}>
                          <Navigation size={12} className="fill-emerald-500" />OK
                        </span>
                      ) : (
                        <button
                          onClick={() => geocodeOneMutation.mutate(c.id)}
                          disabled={geocodeOneMutation.isPending}
                          className="flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 transition-colors"
                          title="Geocodifica questo indirizzo"
                        >
                          <Navigation size={12} />—
                        </button>
                      )}
                    </td>

                    {/* Azioni */}
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {!isReadonly && (
                          <button onClick={() => setEditing(c)}
                            className="btn-ghost px-2 py-1 text-amber-600" title="Modifica">
                            <Pencil size={14} />
                          </button>
                        )}
                        {isAdmin && !isReadonly && (
                          <button
                            onClick={() => confirmDelete(`Eliminare "${c.ragione_sociale || c.comune}"?`, () => deleteMutation.mutate(c.id))}
                            className="btn-ghost px-2 py-1 text-red-500" title="Elimina">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Riga espandibile con le pratiche */}
                  {expanded.has(c.id) && (
                    <PraticheRow key={`pratiche-${c.id}`} clienteId={c.id} colSpan={COL_SPAN} />
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t text-sm text-slate-500">
          <span>{data?.total ?? 0} clienti</span>
        </div>
      </div>

      {showCreate && (
        <CreateClienteModal
          committenti={committenti || []}
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false)
            qc.invalidateQueries({ queryKey: ['clienti-finali'] })
            toast.success('Cliente creato!')
          }}
        />
      )}
      {editing && (
        <EditClienteModal
          cliente={editing}
          committenti={committenti || []}
          onClose={() => setEditing(null)}
          onSuccess={() => {
            setEditing(null)
            qc.invalidateQueries({ queryKey: ['clienti-finali'] })
            toast.success('Cliente aggiornato!')
          }}
        />
      )}
    </div>
  )
}

// ── Modali ────────────────────────────────────────────────────────────────────
function CreateClienteModal({ committenti, onClose, onSuccess }: {
  committenti: { id: number; ragione_sociale: string }[]
  onClose: () => void; onSuccess: () => void
}) {
  const [form, setForm] = useState({
    committente_id: '', nome: '', cognome: '', telefono: '',
    indirizzo: '', numero_civico: '', cap: '', comune: '', provincia: '',
    tipologia_sito: 'residenziale', note_operative: '',
    flag_vip: false, flag_critico: false,
  })
  const f = (k: string, v: string | boolean) => setForm(p => ({ ...p, [k]: v }))

  const mutation = useMutation({
    mutationFn: (data: typeof form) => clientiFinaliApi.create({ ...data, committente_id: Number(data.committente_id) }),
    onSuccess,
    onError: () => toast.error('Errore nella creazione'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b"><h2 className="text-lg font-semibold">Nuovo Cliente Finale</h2></div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form) }} className="p-6 space-y-4">
          <div>
            <label className="label">Committente *</label>
            <select className="select" value={form.committente_id} onChange={(e) => f('committente_id', e.target.value)} required>
              <option value="">Seleziona...</option>
              {committenti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Nome</label><input className="input" value={form.nome} onChange={(e) => f('nome', e.target.value)} /></div>
            <div><label className="label">Cognome</label><input className="input" value={form.cognome} onChange={(e) => f('cognome', e.target.value)} /></div>
            <div><label className="label">Telefono</label><input className="input" value={form.telefono} onChange={(e) => f('telefono', e.target.value)} /></div>
            <div>
              <label className="label">Tipologia Sito</label>
              <select className="select" value={form.tipologia_sito} onChange={(e) => f('tipologia_sito', e.target.value)}>
                {Object.entries(TIPOLOGIE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className="label">Indirizzo *</label><input className="input" value={form.indirizzo} onChange={(e) => f('indirizzo', e.target.value)} required /></div>
            <div><label className="label">CAP</label><input className="input" value={form.cap} onChange={(e) => f('cap', e.target.value)} /></div>
            <div><label className="label">Comune *</label><input className="input" value={form.comune} onChange={(e) => f('comune', e.target.value)} required /></div>
            <div><label className="label">Provincia</label><input className="input" maxLength={2} value={form.provincia} onChange={(e) => f('provincia', e.target.value.toUpperCase())} /></div>
          </div>
          <div><label className="label">Note operative</label><textarea className="input" rows={2} value={form.note_operative} onChange={(e) => f('note_operative', e.target.value)} /></div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.flag_vip} onChange={(e) => f('flag_vip', e.target.checked)} />
              <span className="text-sm text-slate-700">Cliente VIP</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.flag_critico} onChange={(e) => f('flag_critico', e.target.checked)} />
              <span className="text-sm text-slate-700">Sito Critico</span>
            </label>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary flex-1">
              {mutation.isPending ? 'Creazione...' : 'Crea Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditClienteModal({ cliente, committenti, onClose, onSuccess }: {
  cliente: ClienteFinale
  committenti: { id: number; ragione_sociale: string }[]
  onClose: () => void; onSuccess: () => void
}) {
  const [form, setForm] = useState({
    committente_id: String(cliente.committente_id || ''),
    nome: cliente.nome || '',
    cognome: cliente.cognome || '',
    telefono: cliente.telefono || '',
    indirizzo: cliente.indirizzo || '',
    numero_civico: (cliente as any).numero_civico || '',
    cap: cliente.cap || '',
    comune: cliente.comune || '',
    provincia: cliente.provincia || '',
    tipologia_sito: cliente.tipologia_sito || 'residenziale',
    note_operative: (cliente as any).note_operative || '',
    flag_vip: cliente.flag_vip || false,
    flag_critico: cliente.flag_critico || false,
  })
  const f = (k: string, v: string | boolean) => setForm(p => ({ ...p, [k]: v }))

  const mutation = useMutation({
    mutationFn: (data: typeof form) => clientiFinaliApi.update(cliente.id, { ...data, committente_id: Number(data.committente_id) }),
    onSuccess,
    onError: () => toast.error('Errore aggiornamento'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b"><h2 className="text-lg font-semibold">Modifica Cliente Finale</h2></div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form) }} className="p-6 space-y-4">
          <div>
            <label className="label">Committente *</label>
            <select className="select" value={form.committente_id} onChange={(e) => f('committente_id', e.target.value)} required>
              <option value="">Seleziona...</option>
              {committenti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Nome</label><input className="input" value={form.nome} onChange={(e) => f('nome', e.target.value)} /></div>
            <div><label className="label">Cognome</label><input className="input" value={form.cognome} onChange={(e) => f('cognome', e.target.value)} /></div>
            <div><label className="label">Telefono</label><input className="input" value={form.telefono} onChange={(e) => f('telefono', e.target.value)} /></div>
            <div>
              <label className="label">Tipologia Sito</label>
              <select className="select" value={form.tipologia_sito} onChange={(e) => f('tipologia_sito', e.target.value)}>
                {Object.entries(TIPOLOGIE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label className="label">Indirizzo *</label><input className="input" value={form.indirizzo} onChange={(e) => f('indirizzo', e.target.value)} required /></div>
            <div><label className="label">N. Civico</label><input className="input" value={form.numero_civico} onChange={(e) => f('numero_civico', e.target.value)} /></div>
            <div><label className="label">CAP</label><input className="input" value={form.cap} onChange={(e) => f('cap', e.target.value)} /></div>
            <div><label className="label">Comune *</label><input className="input" value={form.comune} onChange={(e) => f('comune', e.target.value)} required /></div>
            <div><label className="label">Provincia</label><input className="input" maxLength={2} value={form.provincia} onChange={(e) => f('provincia', e.target.value.toUpperCase())} /></div>
          </div>
          <div><label className="label">Note operative</label><textarea className="input" rows={2} value={form.note_operative} onChange={(e) => f('note_operative', e.target.value)} /></div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.flag_vip} onChange={(e) => f('flag_vip', e.target.checked)} />
              <span className="text-sm text-slate-700">Cliente VIP</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.flag_critico} onChange={(e) => f('flag_critico', e.target.checked)} />
              <span className="text-sm text-slate-700">Sito Critico</span>
            </label>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary flex-1">
              {mutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
